import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
} from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { FeedbackService } from "src/app/services/feedback.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { MmpConfirmPopupComponent } from "../mmp-confirm-popup/mmp-confirm-popup.component";
import { MESSAGES } from "src/app/shared/constants/messages.constant";
import { AppConfigService } from "src/app/services/app-config.service";
import { Router } from "@angular/router";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ComparisonNormalizer } from "src/app/shared/utils/comparison-normalizer.util";

@Component({
  selector: "app-mmp-feedback",
  templateUrl: "./mmp-feedback.component.html",
  styleUrls: ["./mmp-feedback.component.scss"],
  standalone: true,
  imports: [CommonModule, FormsModule],
})
export class MmpFeedbackComponent implements OnInit, OnDestroy {
  isDislikePopupVisible: boolean = false;
  isLikeSelected: boolean = false;
  isDislikeSelected: boolean = false;
  dislikeFeedback: string = "";
  previousDislikeFeedback: string = "";
  feedbackOptions: string[] = [];
  selectedOption: string = "";
  previousSelectedOption: string = "";
  page: string = "";

  isExtractionVisible = false;
  isEvaluationVisible = false;
  isEquivalencyVisible = false;

  selectedExtractionOption: string | null = null;
  selectedEvaluationOption: string | null = null;
  selectedEquivalencyOption: string | null = null;

  isDislikeExtractionSelected: boolean = false;
  isDislikeEvaluationSelected: boolean = false;
  isDislikeEquivalencySelected: boolean = false;

  isExtractionLikeSelected: boolean = false;
  isLikeEvaluationSelected: boolean = false;
  isLikeEquivalencySelected: boolean = false;

  dislikeExtractionFeedback: string = "";
  dislikeEvaluationFeedback: string = "";
  dislikeEquivalencyFeedback: string = "";

  extractionFeedbackOptions: string[] = [
    "Accuracy Issue",
    "Missing Data",
    "Slow Extraction",
    "Incorrect Extraction",
    "Duplicate Prospects",
    "Others...",
  ];

  evaluationFeedbackOptions: string[] = [
    "Accuracy Issue",
    "Missing Criteria",
    "Slow Evaluation",
    "Incorrect Evaluation",
    "Duplicate Results",
    "Others...",
  ];
  equivalencyFeedbackOptions: string[] = [
    "No equivalent found",
    "Wrong course match",
    "Missing course info",
    "Credit hours mismatch",
    "Duplicate matches shown",
    "Grade mapping error",
    "Others...",
  ];

  feedbackData: { [key: string]: { state: string; feedback: string } } | null =
    null;
  tempFeedbackData: {
    [key: string]: { state: string; feedback: string };
  } | null = null;
  showConsoleLogs: boolean = false;
  ComparisonNormalizer = ComparisonNormalizer;

  @Input() feedBackStage!: string;
  @Input() finalFeedback!: boolean;
  @Output() closeFinalFeedback = new EventEmitter<boolean>();
  @Output() beforeDashboardNavigate = new EventEmitter<void>();

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly feedbackService: FeedbackService,
    private readonly snackBar: MatSnackBar,
    private readonly confirmPopup: MatDialog,
    private readonly envService: AppConfigService,
    private readonly router: Router,
  ) {
    this.showConsoleLogs = this.envService.showConsoleLogs;
  }

  private createDefaultFeedbackData(): {
    [key: string]: { state: string; feedback: string };
  } {
    return {
      extraction: { state: "", feedback: "" },
      evaluation: { state: "", feedback: "" },
      equivalency: { state: "", feedback: "" },
    };
  }

  private normalizeFeedbackData(data: any): {
    [key: string]: { state: string; feedback: string };
  } {
    const defaults = this.createDefaultFeedbackData();
    return {
      extraction: {
        state: data?.extraction?.state ?? defaults.extraction.state,
        feedback: data?.extraction?.feedback ?? defaults.extraction.feedback,
      },
      evaluation: {
        state: data?.evaluation?.state ?? defaults.evaluation.state,
        feedback: data?.evaluation?.feedback ?? defaults.evaluation.feedback,
      },
      equivalency: {
        state: data?.equivalency?.state ?? defaults.equivalency.state,
        feedback: data?.equivalency?.feedback ?? defaults.equivalency.feedback,
      },
    };
  }

  private ensureTempFeedbackData(): void {
    this.tempFeedbackData = this.normalizeFeedbackData(
      this.tempFeedbackData ?? this.feedbackData,
    );
  }

  ngOnInit(): void {
    this.page = ComparisonNormalizer.string(this.feedBackStage).toLowerCase();
    // Set feedback options based on feedBackStage
    if (ComparisonNormalizer.string(this.feedBackStage) === "extraction") {
      this.feedbackOptions = this.extractionFeedbackOptions;
    } else if (
      ComparisonNormalizer.string(this.feedBackStage) === "evaluation"
    ) {
      this.feedbackOptions = this.evaluationFeedbackOptions;
    } else if (
      ComparisonNormalizer.string(this.feedBackStage) === "equivalency"
    ) {
      this.feedbackOptions = this.equivalencyFeedbackOptions;
    }
    // Subscribe to feedback updates
    this.feedbackService.feedback$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        this.feedbackData = this.normalizeFeedbackData(data);

        const pageFeedback = this.page ? this.feedbackData[this.page] : null;
        if (pageFeedback) {
          if (ComparisonNormalizer.string(pageFeedback.state) === "like") {
            this.isLikeSelected = true;
          } else if (
            ComparisonNormalizer.string(pageFeedback.state) === "dislike"
          ) {
            this.isDislikeSelected = true;
          }

          // Check if feedback exists in feedbackOptions
          if (this.feedbackOptions.includes(pageFeedback.feedback)) {
            this.selectedOption = pageFeedback.feedback;
          } else if (pageFeedback.feedback) {
            this.selectedOption = "Others...";
            this.dislikeFeedback = pageFeedback.feedback;
          }
        }

        if (!this.tempFeedbackData) {
          this.tempFeedbackData = structuredClone(this.feedbackData);
          this.modifyToggle();
        }
      });
  }

  modifyToggle() {
    if (!this.tempFeedbackData) {
      return;
    }

    // Update states for Extraction
    if (this.tempFeedbackData["extraction"]) {
      this.isExtractionLikeSelected =
        ComparisonNormalizer.string(
          this.tempFeedbackData["extraction"].state,
        ) === "like";
      this.isDislikeExtractionSelected =
        ComparisonNormalizer.string(
          this.tempFeedbackData["extraction"].state,
        ) === "dislike";
      this.isExtractionVisible =
        ComparisonNormalizer.string(
          this.tempFeedbackData["extraction"].state,
        ) === "dislike";
      if (
        this.extractionFeedbackOptions.includes(
          this.tempFeedbackData["extraction"].feedback,
        )
      ) {
        this.selectedExtractionOption =
          this.tempFeedbackData["extraction"].feedback;
      } else if (this.tempFeedbackData["extraction"].feedback) {
        this.selectedExtractionOption = "Others...";
        this.dislikeExtractionFeedback =
          this.tempFeedbackData["extraction"].feedback;
      }
    }

    // Update states for Evaluation
    if (this.tempFeedbackData["evaluation"]) {
      this.isLikeEvaluationSelected =
        ComparisonNormalizer.string(
          this.tempFeedbackData["evaluation"].state,
        ) === "like";
      this.isDislikeEvaluationSelected =
        ComparisonNormalizer.string(
          this.tempFeedbackData["evaluation"].state,
        ) === "dislike";
      this.isEvaluationVisible =
        ComparisonNormalizer.string(
          this.tempFeedbackData["evaluation"].state,
        ) === "dislike";
      if (
        this.evaluationFeedbackOptions.includes(
          this.tempFeedbackData["evaluation"].feedback,
        )
      ) {
        this.selectedEvaluationOption =
          this.tempFeedbackData["evaluation"].feedback;
      } else if (this.tempFeedbackData["evaluation"].feedback) {
        this.selectedEvaluationOption = "Others...";
        this.dislikeEvaluationFeedback =
          this.tempFeedbackData["evaluation"].feedback;
      }
    }

    // Update states for Equivalency
    if (this.tempFeedbackData["equivalency"]) {
      this.isLikeEquivalencySelected =
        ComparisonNormalizer.string(
          this.tempFeedbackData["equivalency"].state,
        ) === "like";
      this.isDislikeEquivalencySelected =
        ComparisonNormalizer.string(
          this.tempFeedbackData["equivalency"].state,
        ) === "dislike";
      this.isEquivalencyVisible =
        ComparisonNormalizer.string(
          this.tempFeedbackData["equivalency"].state,
        ) === "dislike";
      if (
        this.equivalencyFeedbackOptions.includes(
          this.tempFeedbackData["equivalency"].feedback,
        )
      ) {
        this.selectedEquivalencyOption =
          this.tempFeedbackData["equivalency"].feedback;
      } else if (this.tempFeedbackData["equivalency"].feedback) {
        this.selectedEquivalencyOption = "Others...";
        this.dislikeEquivalencyFeedback =
          this.tempFeedbackData["equivalency"].feedback;
      }
    }
  }

  openDislikePopup() {
    this.previousSelectedOption = this.selectedOption;
    this.previousDislikeFeedback = this.dislikeFeedback;
    this.isDislikePopupVisible = true;
  }

  closeDislikePopup() {
    this.selectedOption = this.previousSelectedOption;
    this.dislikeFeedback = this.previousDislikeFeedback;
    this.isDislikePopupVisible = false;
  }

  selectFeedbackOption(option: string) {
    this.selectedOption = option;
    if (ComparisonNormalizer.string(option) === "others...") {
      this.dislikeFeedback = ""; // Clear feedback for "Others"
    } else {
      this.dislikeFeedback = option; // Pre-fill feedback with the selected option
    }
  }

  submitDislikeFeedback() {
    if (this.feedBackStage) {
      // Emit feedback to the service
      if (ComparisonNormalizer.string(this.feedBackStage) === "extraction") {
        this.feedbackService.setExtractionFeedback({
          state: "Dislike",
          feedback: this.dislikeFeedback,
        });
      } else if (
        ComparisonNormalizer.string(this.feedBackStage) === "evaluation"
      ) {
        this.feedbackService.setEvaluationFeedback({
          state: "Dislike",
          feedback: this.dislikeFeedback,
        });
      } else if (
        ComparisonNormalizer.string(this.feedBackStage) === "equivalency"
      ) {
        this.tempFeedbackData = null;
        this.feedbackService.setEquivalencyFeedback({
          state: "Dislike",
          feedback: this.dislikeFeedback,
        });
      }

      this.showToast("toast-success", "Feedback saved successfully.");
      this.isDislikeSelected = true;
      this.isLikeSelected = false;
      this.isDislikePopupVisible = false;
    } else {
      this.showToast(
        "toast-error",
        "Error selecting feedback. Please try again",
      );
    }
  }

  likeFeedback(): void {
    if (!this.feedBackStage) {
      return;
    }
    if (ComparisonNormalizer.string(this.feedBackStage) === "extraction") {
      this.feedbackService.setExtractionFeedback({
        state: "Like",
        feedback: "",
      });
    } else if (
      ComparisonNormalizer.string(this.feedBackStage) === "evaluation"
    ) {
      this.feedbackService.setEvaluationFeedback({
        state: "Like",
        feedback: "",
      });
    } else if (
      ComparisonNormalizer.string(this.feedBackStage) === "equivalency"
    ) {
      this.tempFeedbackData = null;
      this.feedbackService.setEquivalencyFeedback({
        state: "Like",
        feedback: "",
      });
    }

    this.isLikeSelected = true;
    this.isDislikeSelected = false;
    this.selectedOption = "";
    this.dislikeFeedback = "";
    this.showToast("toast-success", "Feedback saved successfully.");
  }

  showToast(type: string, message: string) {
    this.snackBar.open(message, "x", {
      duration: 3000,
      horizontalPosition: "right",
      verticalPosition: "top",
      panelClass: [type],
    });
  }

  toggleSection(section: string): void {
    this.ensureTempFeedbackData();

    if (ComparisonNormalizer.string(section) === "extraction") {
      this.isExtractionVisible = !this.isExtractionVisible;
      this.isDislikeExtractionSelected = !this.isDislikeExtractionSelected;
      this.isExtractionLikeSelected = false;
      if (!this.isDislikeExtractionSelected) {
        this.tempFeedbackData["extraction"].feedback = "";
        this.tempFeedbackData["extraction"].state = "";
      }
    } else if (ComparisonNormalizer.string(section) === "evaluation") {
      this.isEvaluationVisible = !this.isEvaluationVisible;
      this.isDislikeEvaluationSelected = !this.isDislikeEvaluationSelected;
      this.isLikeEvaluationSelected = false;
      if (!this.isDislikeEvaluationSelected) {
        this.tempFeedbackData["evaluation"].feedback = "";
        this.tempFeedbackData["evaluation"].state = "";
      }
    } else if (ComparisonNormalizer.string(section) === "equivalency") {
      this.isEquivalencyVisible = !this.isEquivalencyVisible;
      this.isDislikeEquivalencySelected = !this.isDislikeEquivalencySelected;
      this.isLikeEquivalencySelected = false;
      if (!this.isDislikeEquivalencySelected) {
        this.tempFeedbackData["equivalency"].feedback = "";
        this.tempFeedbackData["equivalency"].state = "";
      }
    }
  }

  selectExtractionFeedbackOption(option: string): void {
    this.selectedExtractionOption = option;
  }

  selectEvaluationFeedbackOption(option: string): void {
    this.selectedEvaluationOption = option;
  }

  selectEquivalencyFeedbackOption(option: string): void {
    this.selectedEquivalencyOption = option;
  }

  likeExtractionFeedback() {
    this.ensureTempFeedbackData();
    this.tempFeedbackData["extraction"].state = "Like";
    this.isExtractionLikeSelected = true;
    this.isDislikeExtractionSelected = false;
    this.isExtractionVisible = false;
  }

  likeEvaluationFeedback() {
    this.ensureTempFeedbackData();
    this.tempFeedbackData["evaluation"].state = "Like";
    this.isLikeEvaluationSelected = true;
    this.isDislikeEvaluationSelected = false;
    this.isEvaluationVisible = false;
  }

  likeEquivalencyFeedback() {
    this.ensureTempFeedbackData();
    this.tempFeedbackData["equivalency"].state = "Like";
    this.isLikeEquivalencySelected = true;
    this.isDislikeEquivalencySelected = false;
    this.isEquivalencyVisible = false;
    this.isLikeSelected = true;
  }

  closeFinalFeedbackPopup() {
    const dialogRef = this.confirmPopup.open(MmpConfirmPopupComponent, {
      width: "600px",
      data: {
        title: "Confirm To Proceed",
        message: MESSAGES.FEEDBACK.CANCEL_CONFIRM,
        cancelText: "Cancel",
        confirmText: "Continue",
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (ComparisonNormalizer.string(result) === "yes") {
          this.tempFeedbackData = structuredClone(this.feedbackData);
          this.feedbackService.clearFeedback();
          this.feedbackService
            .sendFinalFeedback()
            .pipe(takeUntil(this.destroy$))
            .subscribe(
              (response) => {
                if (
                  ComparisonNormalizer.string(response.message) ===
                  "structured feedback processing completed"
                ) {
                  this.modifyToggle();
                  this.closeFinalFeedback.emit(false);
                  this.showToast(
                    "toast-success",
                    "Session marked as completed successfully",
                  );
                  setTimeout(() => {
                    this.beforeDashboardNavigate.emit();
                    this.router.navigate(["/evaluator-dashboard"]);
                  }, 1200);
                }
              },
              (error) => {
                if (this.showConsoleLogs) {
                  console.error("Error submitting final feedback:", error);
                }
                this.showToast(
                  "toast-error",
                  "Error occured. Please try after sometimes.",
                );
              },
            );
        } else {
          return;
        }
      });
  }

  submitFinalFeedback() {
    this.applyDislikeSelections();
    this.isLikeSelected = false;

    if (!this.tempFeedbackData) {
      this.closeFinalFeedback.emit(false);
      return;
    }

    if (this.hasNoFeedbackSelected(this.tempFeedbackData)) {
      this.showToast(
        "toast-error",
        "Please select Like or Dislike for the feedback stages.",
      );
      return;
    }

    this.commitFeedbackData(this.tempFeedbackData);
    this.sendFinalFeedbackRequest();
    this.closeFinalFeedback.emit(false);
  }

  private applyDislikeSelections(): void {
    this.applyDislikeSelectionForStage(
      "extraction",
      this.isDislikeExtractionSelected,
      this.selectedExtractionOption,
      this.dislikeExtractionFeedback,
    );
    this.applyDislikeSelectionForStage(
      "evaluation",
      this.isDislikeEvaluationSelected,
      this.selectedEvaluationOption,
      this.dislikeEvaluationFeedback,
    );
    this.applyDislikeSelectionForStage(
      "equivalency",
      this.isDislikeEquivalencySelected,
      this.selectedEquivalencyOption,
      this.dislikeEquivalencyFeedback,
    );
  }

  private applyDislikeSelectionForStage(
    stage: "extraction" | "evaluation" | "equivalency",
    isSelected: boolean,
    selectedOption: string | null,
    freeTextFeedback: string,
  ): void {
    if (!isSelected || !this.tempFeedbackData) {
      return;
    }

    this.tempFeedbackData[stage].feedback =
      ComparisonNormalizer.string(selectedOption) === "others..."
        ? freeTextFeedback
        : selectedOption || "";
    this.tempFeedbackData[stage].state = "Dislike";
  }

  private hasNoFeedbackSelected(feedbackData: {
    [key: string]: { state: string; feedback: string };
  }): boolean {
    return Object.keys(feedbackData).every((key) => !feedbackData[key].state);
  }

  private commitFeedbackData(feedbackData: {
    [key: string]: { state: string; feedback: string };
  }): void {
    this.feedbackData = structuredClone(feedbackData);
    this.feedbackService.setExtractionFeedback(this.feedbackData["extraction"]);
    this.feedbackService.setEvaluationFeedback(this.feedbackData["evaluation"]);
    this.feedbackService.setEquivalencyFeedback(
      this.feedbackData["equivalency"],
    );
  }

  private sendFinalFeedbackRequest(): void {
    this.feedbackService
      .sendFinalFeedback()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          if (
            ComparisonNormalizer.string(response.message) ===
            "structured feedback processing completed"
          ) {
            this.showToast(
              "toast-success",
              "Final feedback submitted successfully.",
            );
            setTimeout(() => {
              this.beforeDashboardNavigate.emit();
              this.router.navigate(["/evaluator-dashboard"]);
            }, 1200);
          }
        },
        (error) => {
          if (this.showConsoleLogs) {
            console.error("Error submitting final feedback:", error);
          }
          this.showToast("toast-error", "Failed to submit final feedback.");
        },
      );
  }

  closeFinalFeedbackModal(): void {
    if (this.feedbackData) {
      this.tempFeedbackData = structuredClone(this.feedbackData);
      this.modifyToggle();
    }
    this.closeFinalFeedback.emit(false);
  }

  ngOnDestroy(): void {
    // Complete the destroy$ subject to unsubscribe all subscriptions.
    this.destroy$.next();
    this.destroy$.complete();
  }
}
